import React, { useState } from "react"
import { Container, Table, Badge } from "react-bootstrap"

import Layout from "../../components/layout-docs"
import SEO from "../../components/seo"

import CodeBlock from "../../components/code-block"

import firstRequest from "../../code-blocks/first-request"
import firstResponse from "../../code-blocks/first-response"
import parsingRequest from "../../code-blocks/parsing-request"
import parsingResponse from "../../code-blocks/parsing-response"

import postFormDataRequest from "../../code-blocks/post-form-data"
import postFormDataResponse from "../../code-blocks/post-form-data-response"
import blockCss from "../../code-blocks/block-css"
import blockImages from "../../code-blocks/block-images"
import blockUrl from "../../code-blocks/block-url"
import customHeaders from "../../code-blocks/custom-headers"
import waitForElement from "../../code-blocks/wait-for-element"
import waitForTime from "../../code-blocks/wait-for-time"
import customJavascript from "../../code-blocks/custom-javascript"
import premiumProxies from "../../code-blocks/premium-proxies"
import premiumProxiesLoction from "../../code-blocks/premium-proxies-loction"
import usageEndpointResponse from "../../code-blocks/usage-endpoint-response"

import proxyMode from "../../code-blocks/proxy-mode"
import proxyModeCodeExamples from "../../code-blocks/proxy-mode-examples"

import DataExtractionImage from "../../images/data-extraction.svg"

const codeExampleLanguages = [
  { value: "json", label: "JSON" },
  { value: "curl", label: "CURL" },
  { value: "node", label: "Node JS" },
  { value: "php", label: "PHP" },
  { value: "python", label: "Python" },
  { value: "ruby", label: "Ruby" },
]

const proxyModeCodeExampleLanguages = [
  { value: "curl", label: "CURL" },
  { value: "node", label: "Node JS" },
  { value: "php", label: "PHP" },
  { value: "python", label: "Python" },
]

const proxyModeDefaultCodeExampleLanguage = { value: "curl", label: "CURL" }

const proxyModeExamples = [
  { value: "basic", label: "Default" },
  { value: "render_js", label: "Render JS" },
  { value: "premium_proxies", label: "Premium Proxies" },
  { value: "country_selection", label: "Country Selection" },
]

const proxyModeDefaultExample = { value: "basic", label: "Default" }

const DocsPage = ({ location }) => {
  const [codeLanguage, setCodeLanguage] = useState({
    value: "json",
    label: "JSON",
  })

  return (
    <Layout location={location}>
      <SEO
        title="Documentation"
        description="ScrapeOwl's API allows you to send requests specifying the website you want to scrape and specify the elements you want from those websites."
      />

      <div className="doc-content mt-6">
        <Container>
          <div id="intro">
            <h1 style={{ marginTop: 0 }}>Introduction</h1>

            <div className="section mt-6">
              <h3 className="section-title mb-2">What is the ScrapeOwl API?</h3>
              <p>ScrapeOwl is a simple web scraping and data extraction API.</p>
            </div>

            <div className="section mt-6">
              <h3 className="mb-2">How does it work?</h3>
              <p>
                ScrapeOwl's API allows you to send requests specifying the
                website and its elements you want to scrape.
              </p>
              <div className="illustration">
                <img src={DataExtractionImage} alt="Data Extraction" />
              </div>
            </div>

            <div className="section mt-6">
              <h3>Example</h3>
              <p>
                For example, if you want the content of the h1 and p tags from a
                series of articles on example.com where the h1 tag contains the
                title of the article and the p tags contain the body text.
              </p>

              <p>
                Using ScrapeOwl’s API, you specify example.com as the URL you
                would like to scrape, and h1 and p as the elements you would
                like to parse and retrieve content from.
              </p>
            </div>
          </div>

          <div id="getting-started" className="section mt-6">
            <h2 className="section-title">Getting Started</h2>
            <p>
              The first step before you can start using the ScrapeOwl API is
              signing up and creating an account by going to the{" "}
              <a href="https://app.scrapeowl.com/register">registration page</a>{" "}
              or logging into the{" "}
              <a href="https://app.scrapeowl.com/dashboard">dashboard</a> if you
              already have an account.
            </p>

            <p>
              Once you're in, your API key should be visible on the dashboard
              where you can simply copy and paste it to your requests. It is a
              long string (80 characters) that is a series of random numbers and
              letters that looks something like this:
            </p>

            <code>
              9ijf24fk93rg038jg30rigj394f34f0kh12d12ep3fp24gk3pgk34g23gf74fl430913fj2133f32ffj
            </code>
          </div>

          <div id="api-endpoint" className="section mt-6">
            <h3>Api endpoint</h3>
            <p>The base URL for our scraping API is:</p>
            <strong>https://api.scrapeowl.com/v1/scrape</strong>
            <div
              className="alert alert-custom"
              style={{
                marginTop: "15px !important",
                marginBottom: "0 !important",
              }}
            >
              <strong>
                Note: All response will be in JSON formatted output.
              </strong>
            </div>
          </div>

          <div id="making-first-request" className="section mt-6">
            <h2 className="section-title">Making Your First Request</h2>
            <p>
              APIs are consumed programmatically, meaning you write a program to
              get the data you want from websites you want to scrape and parse.
            </p>
            <p>
              To demonstrate the power of the ScrapeOwl use the example below to
              return a test query using Javascript Object Notation (JSON) to
              make a request to the API.
            </p>

            <h3>Code example</h3>
            <CodeBlock
              code={firstRequest}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="FIRST REQUEST"
              hasLanguageSelector={true}
              hasMultipleLanguages={true}
            />

            <h3>Result</h3>
            <CodeBlock
              code={firstResponse}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title=""
              hasLanguageSelector={false}
            />

            <div id="parameters">
              <h3>
                The following table lists all available ScrapeOwl API parameters
              </h3>

              <div className="table-container">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Default</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          api_key
                        </Badge>
                      </td>
                      <td>string</td>
                      <td>required</td>
                      <td>Your ScrapeOwl API Key</td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          url
                        </Badge>
                      </td>
                      <td>string</td>
                      <td>required</td>
                      <td>Target URL to scrape</td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          elements
                        </Badge>
                      </td>
                      <td>array</td>
                      <td>optional</td>
                      <td>List of elements to extract from the webpage</td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          html
                        </Badge>
                      </td>
                      <td>boolean</td>
                      <td>optional</td>
                      <td>
                        Return page's HTML if set to true. Defaults to false
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          return_headers
                        </Badge>
                      </td>
                      <td>boolean</td>
                      <td>false</td>
                      <td>
                        Return headers returned by the target website server
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          return_cookies
                        </Badge>
                      </td>
                      <td>boolean</td>
                      <td>false</td>
                      <td>
                        Return cookies returned by the target website server
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          cookies
                        </Badge>
                      </td>
                      <td>string</td>
                      <td>optional</td>
                      <td>Cookies headers to send to target URL</td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          headers
                        </Badge>
                      </td>
                      <td>array</td>
                      <td>optional</td>
                      <td>HTTP headers to send to target URL</td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          request_method
                        </Badge>
                      </td>
                      <td>string</td>
                      <td>GET</td>
                      <td>
                        GET, POST, PUT. Default is GET but if you want to
                        POST/PUT data then use appropriate value.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          post_data
                        </Badge>
                      </td>
                      <td>string | array</td>
                      <td>optional</td>
                      <td>POST data on the page</td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          premium_proxies
                        </Badge>
                      </td>
                      <td>boolean</td>
                      <td>optional</td>
                      <td>Use residential proxies to scrape</td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          country
                        </Badge>
                      </td>
                      <td>string</td>
                      <td>optional</td>
                      <td>Set source IP country for residential proxy</td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          render_js
                        </Badge>
                      </td>
                      <td>boolean</td>
                      <td>optional</td>
                      <td>
                        Use headless chrome instance to execute JavaScript
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          custom_js
                        </Badge>
                      </td>
                      <td>string</td>
                      <td>optional</td>
                      <td>
                        Send custom JavaScript to run on the page prior to
                        extracting the HMTL of the page
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          wait_for
                        </Badge>
                      </td>
                      <td>string</td>
                      <td>optional</td>
                      <td>
                        Specify an element that we should wait for to load
                        before scraping the page or an amount time in ms.
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          reject_requests
                        </Badge>
                      </td>
                      <td>array</td>
                      <td>optional</td>
                      <td>Block requests on page</td>
                    </tr>
                    <tr>
                      <td>
                        <Badge className="badge" variant="light">
                          goto_options
                        </Badge>
                      </td>
                      <td>array</td>
                      <td>optional</td>
                      <td>
                        Goto options{" "}
                        <a
                          href="https://pptr.dev/#?product=Puppeteer&version=v2.0.0&show=api-pagegotourl-options"
                          rel="nofollow"
                        >
                          Learn More
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          </div>

          <div id="parsing" className="section mt-6">
            <h2 className="section-title">Extracting Custom Elements</h2>
            <p>
              ScrapeOwl's killer feature is that it's able to extract data from
              pages, so you do not need to parse the page's HTML yourself. Below
              is a table detailing the selection fields.
            </p>

            <h3>ScrapeOwl API Element Selection</h3>

            <div className="table-container">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Default</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <Badge className="badge" variant="light">
                        type
                      </Badge>
                    </td>
                    <td>string</td>
                    <td>css</td>
                    <td>
                      Specify XPath if you are trying to extract using XPath.
                      The default is CSS
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Badge className="badge" variant="light">
                        selector
                      </Badge>
                    </td>
                    <td>string</td>
                    <td>required</td>
                    <td>
                      XPath or css selector for the element you are trying to
                      extract.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Badge className="badge" variant="light">
                        timeout
                      </Badge>
                    </td>
                    <td>integer</td>
                    <td>optional</td>
                    <td>
                      Time in milliseconds to wait for dynamic elements to load
                      on the page
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Badge className="badge" variant="light">
                        html
                      </Badge>
                    </td>
                    <td>boolean</td>
                    <td>false</td>
                    <td>Return the inner HTML of the element</td>
                  </tr>
                  <tr>
                    <td>
                      <Badge className="badge" variant="light">
                        attributes
                      </Badge>
                    </td>
                    <td>boolean | array</td>
                    <td>false</td>
                    <td>Return the attributes of the element.</td>
                  </tr>
                </tbody>
              </Table>
            </div>

            <div className="alert alert-custom mb-3">
              The <Badge className="badge">timeout</Badge> parameter only works
              when <Badge className="badge">render_js</Badge> is set to true
            </div>

            <p>
              If you're making a POST request using JSON, it looks as follows:
            </p>

            <h3>Code example</h3>
            <CodeBlock
              code={parsingRequest}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="EXTRACTING ELEMENTS"
              hasLanguageSelector={true}
              hasMultipleLanguages={true}
            />

            <p>
              The above JSON code shows the request containing element
              attributes with all potential values. The first uses XPath as a
              selector and the second uses the CSS selector. The CSS selector is
              essentially your HTML element name, class, or ID. It's what you
              would write in your stylesheet (CSS).
            </p>

            <h3>Element extraction result</h3>

            <CodeBlock
              code={parsingResponse}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="ELEMENT EXTRACTION"
              hasLanguageSelector={false}
            />

            <p>
              This is an example of a response when extracting all p tags on
              https://example.com where the API extracts all elements that match
              the given conditions. The response contains your data as objects
              where each object contains the text, HTML, or attributes of each
              element.
            </p>
            <p>
              Attributes are key and value pairs of HTML attributes like class
              or any custom data-* attributes set to the particular elements on
              the page.
            </p>
          </div>

          <div id="waiting-for-elements" className="section mt-6">
            <h2 className="section-title">Waiting for Elements to Load</h2>
            <p>
              <Badge className="badge">wait_for</Badge> can be used to make
              ScrapeOwl wait for a given amount of time (in milliseconds) before
              capturing the elements or page's HTML as shown in the following
              example.
            </p>

            <CodeBlock
              code={waitForTime}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="WAIT FOR DURATION"
              hasLanguageSelector={false}
            />

            <p>
              The above code waits for the duration of 6000ms (6 seconds) before
              capturing the elements or page's HTML.
            </p>

            <div className="alert alert-custom">
              <strong>Note:</strong>
              <Badge className="badge">timeout</Badge> set for each element will
              override above value.
            </div>

            <p>
              The <Badge className="badge">wait_for</Badge> can also be used to
              wait for an element to load on the page
            </p>

            <CodeBlock
              code={waitForElement}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="WAIT FOR ELEMENT"
              hasLanguageSelector={false}
            />

            <p>
              The above will wait for all of the p tag elements on the page to
              load before extracting data from the page.
            </p>
            <div className="alert alert-custom">
              <strong>Note:</strong>
              <Badge className="badge">wait_for</Badge> only works when{" "}
              <Badge className="badge">render_js</Badge> is set to true
            </div>
          </div>

          <div id="running-custom-javascript" className="section mt-6">
            <h2 className="section-title">
              Executing Custom JavaScript on the webpage
            </h2>
            <p>
              You can pass custom JavaScript to ScrapeOwl using the{" "}
              <Badge className="badge">custom_js</Badge> attribute, the custom
              JavaScript is executed on the page's console before capturing the
              content.
            </p>
            <p>
              When testing make sure that the JavaScript snippet executes on
              your browser's console, as ScrapeOwl executes the code in the same
              environment.
            </p>
            <p>
              For example, by using the following JavaScript we can scroll the
              page to the bottom of the page.
            </p>

            <CodeBlock
              code={customJavascript}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="CUSTOM JAVASCRIPT"
              hasLanguageSelector={false}
            />

            <div className="alert alert-custom">
              <strong>Note:</strong> Because you can not pass a multi-line
              string in a JSON object, please remove all white spaces and
              convert it to single line before sending.
            </div>

            {/*
              <div className="alert alert-custom">
                <strong>Note:</strong> Because you can not pass a multi-line string in a JSON object, prefer base64 encoding your JS snippet but we also accept it without encoding as well.
              </div>
              */}
          </div>

          <div id="http-headers" className="section mt-6">
            <h2 className="section-title">Setting Custom HTTP Headers</h2>

            <p>
              The <Badge className="badge">headers</Badge> attribute is an
              object containing all of the headers that you want to send to the
              target URL. You can easily set custom HTTP headers to pass along
              with your requests as follows:
            </p>

            <CodeBlock
              code={customHeaders}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="CUSTOM HTTP HEADERS"
              hasLanguageSelector={false}
            />

            <p>
              The above code example sends "Accept Language" HTTP header to all
              outgoing requests.
            </p>
          </div>

          <div id="blocking-resources" className="section mt-6">
            <h2 className="section-title">Blocking Requests on Page</h2>
            <p>
              By using <Badge className="badge">reject_requests</Badge> you can
              easily block any resource of your choice on the page by specifying
              the file format. For example, you can block tracking scripts (.js
              files), images (.png, .jpg, or .jpeg files) or CSS (.css files)
            </p>

            <p>For example, to block all CSS on a page:</p>

            <CodeBlock
              code={blockCss}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="BLOCK CSS"
              hasLanguageSelector={false}
            />

            <p>And if you need to block any PNG and JPG image(s) on a page:</p>

            <CodeBlock
              code={blockImages}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="BLOCK IMAGES"
              hasLanguageSelector={false}
            />

            <p>If you need to block some particular URL on a page</p>

            <CodeBlock
              code={blockUrl}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="BLOCK URLS"
              hasLanguageSelector={false}
            />

            <div className="alert alert-custom">
              <strong>Note:</strong> Blocking the requests only works when{" "}
              <Badge className="badge">render_js</Badge> is set to true
            </div>
          </div>

          <div id="sending-post-requests" className="section mt-6">
            <h2 className="section-title">Posting data</h2>

            <p>
              If you want to send an HTTP POST/PUT request, then set{" "}
              <Badge className="badge">request_method</Badge> to POST or PUT.
            </p>
            <p>
              If you also want to send data with the request, then you can set
              your data in <Badge className="badge">post_data</Badge> attribute.
            </p>

            {/*
                <p>If you need to POST data or submit forms then use the <Badge className="badge">post_data</Badge> attribute.</p>
              */}
            <p>
              When posting data, we try to detect the data type and set an
              appropriate Content-Type header.
            </p>
            <p>
              We set application/json if you set the{" "}
              <Badge className="badge">render_js</Badge> as an array or object.
            </p>
            <p>
              We set application/x-www-form-urlencoded if you set the{" "}
              <Badge className="badge">render_js</Badge> as a string.
            </p>
            <p>
              The Content-Type: application/x-www-form-urlencoded header makes
              the posted data as a normal form submit.
            </p>
            <p>
              The following code example uses POST request to send form data:
            </p>

            <h3>Code example</h3>
            <CodeBlock
              code={postFormDataRequest}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="POST FORM DATA REQUEST"
              hasLanguageSelector={true}
              hasMultipleLanguages={true}
            />

            <h3>Result</h3>
            <CodeBlock
              code={postFormDataResponse}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="POST FORM DATA RESPONSE"
              hasLanguageSelector={false}
            />
            <div className="alert alert-custom">
              <strong>Note:</strong> Above response example is actually parsed
              from ScrapeOwl’s response
            </div>
          </div>

          <div id="sessions">
            <h2 className="section-title">Sessions</h2>
            <p>Documentation coming soon.</p>
          </div>

          <div id="proxies" className="section mt-6">
            <h2 className="section-title">Using Premium Proxies</h2>
            <p>
              By default, we use data center proxies to scrape the data from the
              web. But on some websites that could be difficult to scrape, you
              might need to switch to our premium proxies.
            </p>
            <p>
              Premium proxies use residential IP addresses so they evade
              detection as data center addresses.
            </p>
            <p>
              If you use premium proxies, then each request costs{" "}
              <strong>10</strong> credits.
            </p>
            <p>
              If you use premium proxies with{" "}
              <Badge className="badge" variant="light">
                render_js
              </Badge>
              , then each request costs <strong>25</strong> credits.
            </p>

            <CodeBlock
              code={premiumProxies}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="PREMIUM PROXIES"
              hasLanguageSelector={false}
            />

            <p>
              The default location of proxies is set to the USA by default. You
              can specify the country parameter to other countries to rotate or
              view localized versions as needed.
            </p>

            <CodeBlock
              code={premiumProxiesLoction}
              exampleLanguages={codeExampleLanguages}
              selectedLanguage={codeLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="SET SOURCE COUNTRY IP"
              hasLanguageSelector={false}
            />

            <p>The above example uses IP's from the UK.</p>
            <div className="alert alert-custom">
              <strong>Note:</strong> Using{" "}
              <Badge className="badge" variant="light">
                premium_proxies
              </Badge>{" "}
              costs between <strong>10</strong> or <strong>25</strong> credits
              per request depending on whether{" "}
              <Badge className="badge" variant="light">
                render_js
              </Badge>{" "}
              is used or not.
            </div>

            <h3>List of countries with their ISO codes</h3>

            <div className="table-container">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>ISO code</th>
                    <th>Name</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>br</td>
                    <td>Brazil</td>
                  </tr>
                  <tr>
                    <td>ca</td>
                    <td>Canada</td>
                  </tr>
                  <tr>
                    <td>fr</td>
                    <td>France</td>
                  </tr>
                  <tr>
                    <td>de</td>
                    <td>Germany</td>
                  </tr>
                  <tr>
                    <td>ge</td>
                    <td>Greece</td>
                  </tr>
                  <tr>
                    <td>il</td>
                    <td>Israel</td>
                  </tr>
                  <tr>
                    <td>it</td>
                    <td>Italy</td>
                  </tr>
                  <tr>
                    <td>mx</td>
                    <td>Mexico</td>
                  </tr>
                  <tr>
                    <td>nl</td>
                    <td>Netherlands</td>
                  </tr>
                  <tr>
                    <td>ru</td>
                    <td>Russia</td>
                  </tr>
                  <tr>
                    <td>es</td>
                    <td>Spain</td>
                  </tr>
                  <tr>
                    <td>se</td>
                    <td>Sweden</td>
                  </tr>
                  <tr>
                    <td>gb</td>
                    <td>United Kingdom</td>
                  </tr>
                  <tr>
                    <td>us</td>
                    <td>United States</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          <div id="proxy-mode">
            <h2 className="section-title">
              Proxy Mode <sup>Beta</sup>
            </h2>
            <p>ScrapeOwl can be used just like any other proxies.</p>
            <p>
              When used in proxy mode, the data parsing (elements) feature is
              not usable.
            </p>
            <p>
              To use ScrapeOwl as a proxy, copy the following code, and set your
              API key.
            </p>

            <h4 style={{ marginBottom: 0 }}>API parameters example</h4>
            <CodeBlock
              code={proxyMode}
              exampleLanguages={proxyModeExamples}
              selectedLanguage={proxyModeDefaultExample}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title=""
              hasLanguageSelector={true}
              hasMultipleLanguages={true}
            />

            <h4 style={{ marginBottom: 0 }}>Code example</h4>
            <CodeBlock
              code={proxyModeCodeExamples}
              exampleLanguages={proxyModeCodeExampleLanguages}
              selectedLanguage={proxyModeDefaultCodeExampleLanguage}
              handleLanguageSelect={selectedOption => {
                setCodeLanguage(selectedOption)
              }}
              title="Code Example"
              hasLanguageSelector={true}
              hasMultipleLanguages={true}
              defaultSelectedTab="curl"
            />
          </div>

          <div id="request-calculation" className="section mt-6">
            <h2 className="section-title">API Request Cost Calculation</h2>

            <p>
              Because ScrapeOwl has multiple features, the credit consumption
              per request depends on the specifics of your request and which
              attributes you're sending along.
            </p>

            <p>
              The{" "}
              <Badge className="badge" variant="light">
                request_cost
              </Badge>{" "}
              attribute in the response shows the cost in credit that the
              request costed.
            </p>

            <h3>Credit consumption per request type is as follows</h3>

            <div className="table-container">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      A request using rotating proxies without{" "}
                      <Badge className="badge" variant="light">
                        render_js
                      </Badge>
                    </td>
                    <td>
                      <strong>1</strong> credit
                    </td>
                  </tr>
                  <tr>
                    <td>
                      A request using rotating proxies with{" "}
                      <Badge className="badge" variant="light">
                        render_js
                      </Badge>
                    </td>
                    <td>
                      <strong>5</strong> credits
                    </td>
                  </tr>
                  <tr>
                    <td>
                      A request using premium proxies without{" "}
                      <Badge className="badge" variant="light">
                        render_js
                      </Badge>
                    </td>
                    <td>
                      <strong>10</strong> credits
                    </td>
                  </tr>
                  <tr>
                    <td>
                      A request using premium proxies with{" "}
                      <Badge className="badge" variant="light">
                        render_js
                      </Badge>
                    </td>
                    <td>
                      <strong>25</strong> credits
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          <div id="response-codes" className="section mt-6">
            <h2 className="section-title">API Response Status Codes</h2>

            <p>
              The response status code is set in the header as well as in the
              JSON object that you get in response.
            </p>
            <p>
              The{" "}
              <Badge className="badge" variant="light">
                is_billed
              </Badge>{" "}
              attribute shows if the request was billed against credits in your
              account.
            </p>

            <h3>The ScrapeOwl API uses the following response status codes</h3>
            <div className="table-container">
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Response Code</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      200{" "}
                      <Badge className="badge" variant="light">
                        billed
                      </Badge>
                    </td>
                    <td>Successful Call</td>
                  </tr>
                  <tr>
                    <td>400</td>
                    <td>Bad Request</td>
                  </tr>
                  <tr>
                    <td>401</td>
                    <td>
                      Unauthorized - Your API key is wrong or you haven't
                      provided one
                    </td>
                  </tr>
                  <tr>
                    <td>403</td>
                    <td>
                      Forbidden - You do not have enough credits left for this
                      API call
                    </td>
                  </tr>
                  <tr>
                    <td>
                      404{" "}
                      <Badge className="badge" variant="light">
                        billed
                      </Badge>
                    </td>
                    <td>Page Not found</td>
                  </tr>
                  <tr>
                    <td>429</td>
                    <td>
                      Too Many Concurrent Requests -- You consumed all of
                      allowed concurrent requests. <br /> Please upgrade or
                      contact support to increase the limit.
                    </td>
                  </tr>
                  <tr>
                    <td>500</td>
                    <td>Internal Error, Please try again.</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          <div id="credits" className="section mt-6">
            <h2 className="section-title">API response status codes</h2>

            <p>
              If you would like to check the stats about credit usage in your
              account, send a GET request to the following endpoint:
            </p>
            <div
              className="alert alert-custom"
              style={{ marginTop: 15, marginBottom: 15 }}
            >
              <strong>https://api.scrapeowl.com/v1/usage</strong>
            </div>
          </div>

          <p>
            Requests to the usage endpoint are limited to <strong>20</strong>{" "}
            requests per minute.
          </p>

          <h4 style={{ marginTop: 15, marginBottom: 0 }}>
            The following JSON shows example response from /usage endpoint
          </h4>
          <CodeBlock
            code={usageEndpointResponse}
            exampleLanguages={codeExampleLanguages}
            selectedLanguage={codeLanguage}
            handleLanguageSelect={selectedOption => {
              setCodeLanguage(selectedOption)
            }}
            title="USAGE ENDPOINT RESPONSE"
            hasLanguageSelector={false}
            boxStyle={{ marginTop: "0.5em" }}
          />

          <div
            className="alert alert-custom"
            style={{ marginTop: 15, marginBottom: 0 }}
          >
            <strong>
              If you have any questions, reach out to{" "}
              <a href="mailto:support@scrapeowl.com">support@scrapeowl.com</a>,
              and we will be more than happy to help you out.
            </strong>
          </div>
        </Container>
      </div>
    </Layout>
  )
}

export default DocsPage
